import { DEFAULT_SIM } from '../features/FloodSimSettings/config/floodSimConfig';
import {
  addUpstream,
  buildLink,
  deviceDistanceMiles,
  deviceLabel,
  routingOf,
  travelMinutes,
  upstreamLinksOf,
} from '../features/FloodSimSettings/utils/floodSimRouting.utils';

export const getChangedSettings = (original = {}, updated = {}) => {
  const changes = {};

  for (const key in updated) {
    // Identifiers and non-editable flags never belong in the settings PATCH.
    if (key === 'node_id' || key === 'uuid' || key === 'imei' || key === 'resetCalibration') {
      continue;
    }

    if (key === 'reboot' || key === 'imageRequest' || key === 'gpsSync') {
      if (updated[key]) changes[key] = updated[key];
      continue;
    }

    if (key === 'sim') {
      if (JSON.stringify(original[key]) !== JSON.stringify(updated[key])) {
        changes[key] = updated[key];
      }
      continue;
    }

    if (
      original[key] === null &&
      updated[key] === 0 &&
      (key === 'ffTheshold' || key === 'ffiImgEnable')
    ) {
      continue;
    }

    if (updated[key] !== original[key]) {
      changes[key] = updated[key];
    }
  }

  // Simulator bookkeeping is not a setting. Copy before stripping it because
  // the draft may still reference the frozen RTK Query cache object.
  if (changes.sim && typeof changes.sim === 'object' && 'cursor_state' in changes.sim) {
    const { cursor_state: _cursor, ...sim } = changes.sim;
    changes.sim = sim;
  }
  return changes;
};

/** Plan the preview and PATCH payloads from the same device-list/draft snapshot. */
export const buildSavePlan = ({
  device,
  deviceSettings,
  changedSettings,
  pendingDownstream,
  connectedDevices,
}) => {
  const summary = [];
  const downstreamSaves = [];
  if (!device) return { selectedDeviceSave: null, downstreamSaves, summary };

  const selectedDeviceName = deviceLabel(device);
  const nameOf = (id) => deviceLabel(connectedDevices.find((entry) => entry.id === id) ?? { id });
  const selectedDeviceSave = Object.keys(changedSettings).length
    ? { deviceId: device.id, deviceName: selectedDeviceName, deviceSettings: changedSettings }
    : null;
  const fieldKeys = Object.keys(changedSettings).filter((key) => key !== 'sim');
  const parts = [];
  if (fieldKeys.length) parts.push(fieldKeys.join(', '));
  if (changedSettings.sim) {
    const before = upstreamLinksOf(device);
    const after = upstreamLinksOf({ ...device, device_settings: { sim: deviceSettings.sim } });
    const added = after.filter((link) => !before.some((previous) => previous.uid === link.uid));
    const removed = before.filter((previous) => !after.some((link) => link.uid === previous.uid));
    const changedSpeed = after.filter((link) =>
      before.some(
        (previous) =>
          previous.uid === link.uid &&
          Math.round(previous.travelMinutes) !== Math.round(link.travelMinutes),
      ),
    );
    parts.push('demo settings');
    added.forEach((link) =>
      parts.push(
        `${nameOf(link.uid)} now feeds into ${selectedDeviceName} (about ${Math.round(link.travelMinutes)} min)`,
      ),
    );
    removed.forEach((link) =>
      parts.push(`${nameOf(link.uid)} no longer feeds into ${selectedDeviceName}`),
    );
    changedSpeed.forEach((link) =>
      parts.push(
        `the link from ${nameOf(link.uid)} is now about ${Math.round(link.travelMinutes)} min`,
      ),
    );
  }
  if (parts.length) summary.push({ device: selectedDeviceName, what: parts.join('; ') });

  // The selected draft supplies the scenario and upstream routing to every target.
  const self = {
    ...device,
    device_settings: { ...device.device_settings, sim: deviceSettings.sim },
  };
  const lookup = (id) =>
    id === device.id ? self : connectedDevices.find((entry) => entry.id === id);
  const selectedSim = deviceSettings.sim ?? DEFAULT_SIM;
  for (const pendingLink of pendingDownstream) {
    const target = connectedDevices.find((entry) => entry.id === pendingLink.id);
    const minutes = travelMinutes(deviceDistanceMiles(device, target), pendingLink.speedMph);
    summary.push({
      device: nameOf(pendingLink.id),
      what: `${selectedDeviceName} now feeds into it (about ${Math.round(minutes ?? 0)} min at ${pendingLink.speedMph} mph); it takes this scenario`,
    });
    if (!target) continue;

    const built = buildLink({ upstream: self, downstream: target, speedMph: pendingLink.speedMph });
    const routing = addUpstream(routingOf(target), built.link, target, lookup);
    const { cursor_state: _cursor, ...targetSim } = target.device_settings?.sim ?? DEFAULT_SIM;
    downstreamSaves.push({
      deviceId: target.id,
      deviceName: deviceLabel(target),
      pendingLink,
      deviceSettings: {
        sim: {
          ...targetSim,
          sim_type: 'scenario',
          scenario_name: selectedSim.scenario_name,
          scenario_params: selectedSim.scenario_params ?? {},
          routing,
        },
      },
    });
  }

  return { selectedDeviceSave, downstreamSaves, summary };
};
